import styled from "styled-components";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import heroImage from "../assets/images/hero-chandelier.jpeg";
import aboutImage from "../assets/images/about-feature.jpeg";
import Button from "../components/common/Button";
import { useEffect, useState, useMemo } from "react";
import { products } from "../data/products";
import LoadingScreen from "../components/common/LoadingScreen";
import { HashLink } from "react-router-hash-link";
import { useDelayedLoading } from "../hooks/useDelayedLoading";
import ErrorScreen from "../components/common/ErrorScreen";
import { testimonials } from "../data/testimonials";
import { featuredItems } from "../data/featuredItems";
import SEO from "../components/SEO";
import StructuredData, {
  OrganizationSchema,
  LocalBusinessSchema,
} from "../components/StructuredData";

const Home = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Collect all images that need to be preloaded
  const imagesToPreload = useMemo(
    () => [
      heroImage,
      aboutImage,
      ...featuredItems.map((item) => item.image),
      ...products.map((product) => product.image),
    ],
    []
  );

  // Create loading promises for all images
  const loadingPromises = useMemo(() => {
    return imagesToPreload.map((imageUrl) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = imageUrl;
        img.onload = resolve;
        img.onerror = resolve;
      });
    });
  }, [imagesToPreload]);

  // Use the delayed loading hook
  const { isLoading, error } = useDelayedLoading(loadingPromises);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle product slider auto-scroll
  useEffect(() => {
    const maxIndex = products.length - (isMobile ? 1 : 3);
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex >= maxIndex ? 0 : prevIndex + 1
      );
    }, 3000);
    return () => clearInterval(timer);
  }, [isMobile]);

  // Handle scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const totalScroll =
        document.documentElement.scrollHeight - window.innerHeight;
      const currentProgress = (window.pageYOffset / totalScroll) * 100;
      setScrollProgress(currentProgress);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (error) {
    return <ErrorScreen error={error} />;
  }

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <>
      <SEO
        title="Luxury Crystal Chandeliers & Lighting Solutions"
        description="Transform your space with Chandelier Luxe's exquisite crystal chandeliers. 25+ years of excellence, 1000+ installations. Professional installation, restoration, and maintenance services in Johannesburg, South Africa. Premium crystals from Swarovski and Asfour available."
        keywords="crystal chandeliers, luxury lighting Johannesburg, chandelier installation South Africa, crystal lighting, Swarovski chandeliers, luxury home lighting, chandelier restoration, custom chandeliers"
        url="/"
        image="/assets/images/hero-chandelier.jpeg"
      />
      <StructuredData data={OrganizationSchema} />
      <StructuredData data={LocalBusinessSchema} />
      <HomeContainer>
      <ScrollProgressBar style={{ width: `${scrollProgress}%` }} />
      <HeroSection>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2 }}
        >
          <h1>Chandelier Luxe</h1>
          <p>Transform your space with our exquisite lighting solutions</p>
          <HeroButton as={Link} to="/products">
            Explore Collection
          </HeroButton>
        </motion.div>
      </HeroSection>

      <ProductsSliderSection>
        <h2>Featured Products</h2>
        <ProductsSliderContainer>
          <ProductsSlider
            style={{
              transform: `translateX(-${
                currentIndex * (isMobile ? 100 : 33.333)
              }%)`,
            }}
          >
            {products.map((product) => (
              <ProductSlide key={product.id}>
                <Link
                  to={`/product/${product.id}`}
                  style={{
                    textDecoration: "none",
                    height: "100%",
                    display: "block",
                  }}
                >
                  <ProductImage src={product.image} alt={product.name} />
                  <ProductInfo>
                    <h3>{product.name}</h3>
                    <p>{product.category}</p>
                  </ProductInfo>
                </Link>
              </ProductSlide>
            ))}
          </ProductsSlider>
        </ProductsSliderContainer>
        <ExploreMoreLink to="/products">
          Explore More Products
          <span>→</span>
        </ExploreMoreLink>
      </ProductsSliderSection>

      <FeaturedSection>
        <FeaturedGrid>
          {featuredItems.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.5,
                ease: "easeOut",
              }}
            >
              <FeaturedCard to={item.link}>
                <FeaturedImage>
                  <img src={item.image} alt={item.title} />
                </FeaturedImage>
                <FeaturedContent>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <FeaturedButton>
                    {item.buttonText}
                    <span>→</span>
                  </FeaturedButton>
                </FeaturedContent>
              </FeaturedCard>
            </motion.div>
          ))}
        </FeaturedGrid>
      </FeaturedSection>

      <AboutSection>
        <AboutContent>
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Our Legacy of Elegance</h2>
            <p>
              With over 25 years of experience and more than 1,000
              installations, Chandelier Luxe specializes in the sale,
              installation, repair, and restoration of exquisite crystal
              chandeliers. We offer a wide range of styles and, on request, can
              source premium crystals from brands like Asfour and Swarovski.
            </p>
            <p>
              Our skilled team ensures meticulous installation and restoration,
              bringing out the brilliance of each chandelier. At Chandelier
              Luxe, we deliver luxury, craftsmanship, and timeless beauty to
              illuminate your space.
            </p>
            <AboutFeatures>
              <FeatureItem>
                <span>25+</span>
                Years of Excellence
              </FeatureItem>
              <FeatureItem>
                <span>1000+</span>
                Installations
              </FeatureItem>
              <FeatureItem>
                <span>100%</span>
                Client Satisfaction
              </FeatureItem>
            </AboutFeatures>
            <Link to="/about">
              <Button>Discover Our Story</Button>
            </Link>
          </motion.div>
        </AboutContent>
        <AboutImageWrapper>
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <img src={aboutImage} alt="Crafting Excellence" />
          </motion.div>
        </AboutImageWrapper>
      </AboutSection>

      <TestimonialsSection>
        <SectionHeader>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Client Testimonials</h2>
            <p>Illuminating spaces and exceeding expectations</p>
          </motion.div>
        </SectionHeader>

        <TestimonialsGrid>
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.2 }}
            >
              <TestimonialCard>
                <QuoteIcon>"</QuoteIcon>
                <TestimonialContent>{testimonial.content}</TestimonialContent>
                <TestimonialFooter>
                  <TestimonialAuthor>
                    <AuthorInfo>
                      <h4>{testimonial.name}</h4>
                      <span>{testimonial.role}</span>
                      <StarRating>
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i}>★</Star>
                        ))}
                      </StarRating>
                    </AuthorInfo>
                  </TestimonialAuthor>
                </TestimonialFooter>
              </TestimonialCard>
            </motion.div>
          ))}
        </TestimonialsGrid>

        <TestimonialsCTA>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p>Join our growing list of satisfied clients</p>
            <Link to="/contact">
              <Button>Schedule a Consultation</Button>
            </Link>
          </motion.div>
        </TestimonialsCTA>
      </TestimonialsSection>
    </HomeContainer>
    </>
  );
};

const HomeContainer = styled.div`
  width: 100%;
`;

const HeroSection = styled.section`
  height: 100vh;
  background: linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7)),
    url(${heroImage}) center/cover no-repeat;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  text-align: center;
  position: relative;

  div {
    position: relative;
    z-index: 2;
    padding: 0 2rem;
    margin-bottom: 4vh;
    max-width: 1000px;
  }

  h1 {
    font-size: 4.5rem;
    font-family: "Cormorant Garamond", serif;
    font-weight: 300;
    color: #ffffff;
    margin-bottom: 1.5rem;
    line-height: 1.2;
    letter-spacing: 2px;
    text-transform: uppercase;
    background: linear-gradient(45deg, #ffffff 0%, #e2c6a1 60%, #c0a080 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text-shadow: 0 0 40px rgba(192, 160, 128, 0.4),
      0 0 20px rgba(255, 255, 255, 0.2);

    @media (max-width: 768px) {
      font-size: 2.8rem;
    }
  }

  p {
    font-size: 1.8rem;
    font-family: "Playfair Display", serif;
    font-weight: 300;
    color: #e2c6a1;
    margin-bottom: 3rem;
    letter-spacing: 4px;
    text-transform: uppercase;
    opacity: 0.95;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5), 0 0 20px rgba(192, 160, 128, 0.3);

    @media (max-width: 768px) {
      font-size: 1.4rem;
      letter-spacing: 2px;
    }
  }
`;

const HeroButton = styled.button`
  padding: 1.2rem 3rem;
  font-size: 1.1rem;
  background: transparent;
  border: 1px solid #c0a080;
  color: #c0a080;
  font-family: "Montserrat", sans-serif;
  cursor: pointer;
  transition: all 0.4s ease;
  text-transform: uppercase;
  letter-spacing: 3px;
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: 0.5s;
  }

  &:hover {
    background: #c0a080;
    color: #1a1a1a;
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);

    &:before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.2);
  }
`;

const FeaturedSection = styled.section`
  max-width: 1800px;
  margin: 8rem auto;
  padding: 0 2rem;
  text-align: center;

  h2 {
    text-align: center;
    margin-bottom: 5rem;
    font-size: 3.5rem;
    color: ${(props) => props.theme.colors.accent};
    font-family: "Cormorant Garamond", serif;
    position: relative;

    &::after {
      content: "";
      position: absolute;
      bottom: -20px;
      left: 50%;
      transform: translateX(-50%);
      width: 100px;
      height: 2px;
      background: ${(props) => props.theme.colors.accent};
    }
  }
`;

const FeaturedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 4rem;
  padding: 0 2rem;
  justify-content: center;
  max-width: 1200px;
  margin: 0 auto;

  @media (min-width: 1400px) {
    grid-template-columns: repeat(2, minmax(400px, 600px));
  }

  @media (max-width: 450px) {
    grid-template-columns: 1fr;
    padding: 0 1rem;
  }
`;

const FeaturedCard = styled(HashLink)`
  display: block;
  text-decoration: none;
  background: rgba(44, 44, 44, 0.8);
  border: 1px solid rgba(192, 160, 128, 0.2);
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  height: 100%;
  will-change: transform;
  backface-visibility: hidden;
  transform: translateZ(0);

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3),
      0 0 30px rgba(192, 160, 128, 0.1);
    border-color: ${(props) => props.theme.colors.accent};
  }
`;

const FeaturedImage = styled.div`
  position: relative;
  height: 300px;
  overflow: hidden;
  background: ${(props) => props.theme.colors.primary};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
    transform: translateZ(0);
    will-change: transform;
  }

  ${FeaturedCard}:hover img {
    transform: scale(1.05);
  }
`;

const FeaturedContent = styled.div`
  padding: 2rem;

  h3 {
    font-size: 1.8rem;
    color: ${(props) => props.theme.colors.accent};
    margin-bottom: 1rem;
    font-family: "Cormorant Garamond", serif;
  }

  p {
    color: ${(props) => props.theme.colors.textSecondary};
    margin-bottom: 1.5rem;
    line-height: 1.6;
  }
`;

const FeaturedButton = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${(props) => props.theme.colors.accent};
  font-weight: 500;

  span {
    transition: transform 0.3s ease;
  }

  ${FeaturedCard}:hover & span {
    transform: translateX(5px);
  }
`;

const AboutSection = styled.section`
  max-width: ${(props) => props.theme.maxWidth};
  margin: 6rem auto;
  padding: 0 2rem;

  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AboutContent = styled.div`
  padding: 2rem 0;

  h2 {
    font-size: 2.5rem;
    color: ${(props) => props.theme.colors.accent};
    margin-bottom: 1.5rem;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.8;
    margin-bottom: 2rem;
    color: ${(props) => props.theme.colors.textSecondary};
  }
`;

const AboutFeatures = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 2.5rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FeatureItem = styled.div`
  text-align: left;
  font-size: 1rem;
  color: ${(props) => props.theme.colors.textSecondary};

  span {
    display: block;
    font-size: 2.5rem;
    font-family: "Cormorant Garamond", serif;
    color: ${(props) => props.theme.colors.accent};
    margin-bottom: 0.5rem;
  }
`;

const AboutImageWrapper = styled.div`
  position: relative;
  height: 100%;
  min-height: 500px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }

  &::after {
    content: "";
    position: absolute;
    top: 20px;
    right: -20px;
    width: 100%;
    height: 100%;
    border: 2px solid ${(props) => props.theme.colors.accent};
    border-radius: 8px;
    z-index: -1;
  }

  @media (max-width: 968px) {
    min-height: 400px;
  }
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
`;

const ProductInfo = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.5rem;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  transition: background 0.3s ease;

  h3 {
    margin: 0;
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    opacity: 0.8;
  }
`;

const ProductSlide = styled.div`
  flex: 0 0 calc(33.333% - 1.33rem);
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  height: 500px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  transition: all 0.3s ease;
  cursor: pointer;

  @media (max-width: 768px) {
    flex: 0 0 100%;
    margin: 0;
    height: 400px;
    border-radius: 0;
  }

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.2);

    ${ProductInfo} {
      background: rgba(0, 0, 0, 0.8);
    }

    ${ProductImage} {
      transform: scale(1.05);
    }
  }

  a {
    color: inherit;
    text-decoration: none;
  }
`;

const ProductsSlider = styled.div`
  display: flex;
  transition: transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
  width: 100%;
  gap: 2rem;

  @media (max-width: 768px) {
    gap: 0;
  }
`;

const ProductsSliderContainer = styled.div`
  width: 100%;
  overflow: hidden;
  position: relative;
  padding: 2rem 0;
  margin: 0 -1rem;

  @media (max-width: 768px) {
    padding: 0;
    margin: 0;
  }
`;

const ProductsSliderSection = styled.section`
  max-width: 1800px;
  margin: 8rem auto;
  padding: 0 4rem;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 0 1rem;
    margin: 4rem auto;
  }

  h2 {
    text-align: center;
    margin-bottom: 5rem;
    font-size: 3.5rem;
    color: ${(props) => props.theme.colors.accent};
    font-family: "Cormorant Garamond", serif;
    position: relative;

    @media (max-width: 768px) {
      font-size: 2.5rem;
      margin-bottom: 3rem;
    }

    &::after {
      content: "";
      position: absolute;
      bottom: -20px;
      left: 50%;
      transform: translateX(-50%);
      width: 100px;
      height: 2px;
      background: ${(props) => props.theme.colors.accent};
    }
  }
`;

const ExploreMoreLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  margin: 4rem auto 0;
  font-size: 1.2rem;
  color: ${(props) => props.theme.colors.accent};
  text-decoration: none;
  transition: all 0.3s ease;
  padding: 1rem 2rem;
  border: 1px solid ${(props) => props.theme.colors.accent};
  border-radius: 4px;
  width: fit-content;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;

  span {
    transition: transform 0.3s ease;
  }

  &:hover {
    background: ${(props) => props.theme.colors.accent};
    color: #fff;

    span {
      transform: translateX(5px);
    }
  }
`;

const ScrollProgressBar = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 4px;
  background: ${(props) => props.theme.colors.accent};
  width: ${(props) => props.width}%;
  z-index: 1000;
`;

const TestimonialsSection = styled.section`
  background: linear-gradient(
    to bottom,
    ${(props) => props.theme.colors.primary},
    rgba(44, 44, 44, 0.95)
  );
  padding: 8rem 2rem;
  margin-top: 8rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 5rem;

  h2 {
    font-size: 3.5rem;
    color: ${(props) => props.theme.colors.accent};
    font-family: "Cormorant Garamond", serif;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.2rem;
    color: ${(props) => props.theme.colors.textSecondary};
    font-family: "Playfair Display", serif;
    font-style: italic;
  }
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 3rem;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const TestimonialCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(192, 160, 128, 0.2);
  border-radius: 16px;
  padding: 3rem 2rem 2rem;
  position: relative;
  backdrop-filter: blur(10px);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  }
`;

const QuoteIcon = styled.div`
  position: absolute;
  top: -20px;
  left: 30px;
  font-size: 8rem;
  color: ${(props) => props.theme.colors.accent};
  opacity: 0.2;
  font-family: "Georgia", serif;
`;

const TestimonialContent = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${(props) => props.theme.colors.textSecondary};
  margin-bottom: 2rem;
  position: relative;
  font-style: italic;
`;

const TestimonialFooter = styled.div`
  border-top: 1px solid rgba(192, 160, 128, 0.2);
  padding-top: 1.5rem;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const AuthorInfo = styled.div`
  h4 {
    font-size: 1.2rem;
    color: ${(props) => props.theme.colors.accent};
    margin-bottom: 0.2rem;
    font-family: "Cormorant Garamond", serif;
  }

  span {
    font-size: 0.9rem;
    color: ${(props) => props.theme.colors.textSecondary};
    display: block;
    margin-bottom: 0.5rem;
  }
`;

const StarRating = styled.div`
  display: flex;
  gap: 2px;
`;

const Star = styled.span`
  color: ${(props) => props.theme.colors.accent};
  font-size: 1rem;
`;

const TestimonialsCTA = styled.div`
  text-align: center;
  margin-top: 5rem;

  p {
    font-size: 1.2rem;
    color: ${(props) => props.theme.colors.textSecondary};
    margin-bottom: 2rem;
  }
`;

export default Home;
